import { useState, useRef, useEffect, FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type FormData = {
  name: string;
  email: string;
  phone: string;
  gender: string;
  birthday: string;
  ageConfirm: boolean;
  terms: boolean;
  marketing: boolean;
};

const benefits = [
  "2 months free premium access!",
  "First in line when we launch",
  "Exclusive early adopter badge",
  "Shape the product with your feedback",
];

export default function RegisterPage() {
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const googleReCaptcha = useGoogleReCaptcha();

  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    gender: "",
    birthday: "",
    ageConfirm: false,
    terms: false,
    marketing: false,
  });

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("t");
    const name = params.get("name");
    const email = params.get("email");
    const gender = params.get("gender");

    const normalizedGender =
      gender === "男" ? "Man" : gender === "女" ? "Woman" : typeof gender === "string" ? gender : null;

    const applyPrefill = (prefill: { name?: string; email?: string; gender?: string }) => {
      const g =
        prefill.gender === "男"
          ? "Man"
          : prefill.gender === "女"
            ? "Woman"
            : typeof prefill.gender === "string"
              ? prefill.gender
              : null;

      setForm((prev) => ({
        ...prev,
        name: prev.name || prefill.name || "",
        email: prev.email || prefill.email || "",
        gender: prev.gender || g || "",
      }));
    };

    if (token) {
      fetch(`/api/prefill?t=${encodeURIComponent(token)}`)
        .then(async (r) => {
          if (!r.ok) throw new Error("bad");
          return (await r.json()) as { ok: boolean; prefill?: { name?: string; email?: string; gender?: string } };
        })
        .then((data) => {
          if (data.ok && data.prefill) applyPrefill(data.prefill);
        })
        .catch(() => {
          applyPrefill({ name: name || undefined, email: email || undefined, gender: normalizedGender || undefined });
        });
      return;
    }

    applyPrefill({ name: name || undefined, email: email || undefined, gender: normalizedGender || undefined });
  }, [location.search]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isAtLeast18 = (birthdayStr: string) => {
    const birthDate = new Date(birthdayStr);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!form.birthday || !isAtLeast18(form.birthday)) {
      setError("You must be 18 years of age or older to join Aurays.");
      return;
    }
    if (!form.ageConfirm) {
      setError("Please confirm you are 18 or older.");
      return;
    }
    if (!form.terms) {
      setError("Please accept the Terms & Conditions.");
      return;
    }

    setLoading(true);

    const isRecaptchaEnabled = !!(import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined);
    let recaptchaToken = "";
    if (isRecaptchaEnabled) {
      if (!googleReCaptcha?.executeV3) {
        setError("reCAPTCHA is not ready. Please try again.");
        setLoading(false);
        return;
      }
      try {
        recaptchaToken = await googleReCaptcha.executeV3("register");
      } catch {
        setError("reCAPTCHA is not ready. Please try again.");
        setLoading(false);
        return;
      }
    }

    const body = new URLSearchParams({
      name: form.name,
      email: form.email,
      phone: form.phone,
      gender: form.gender,
      birthday: form.birthday,
      ageConfirm: form.ageConfirm ? "Yes" : "No",
      terms: form.terms ? "Accepted" : "Not accepted",
      marketing: form.marketing ? "Opted in" : "Opted out",
      recaptchaToken,
    });

    try {
      const res = await fetch("/api/form/register", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      if (!res.ok || !data?.ok) {
        if (data?.error === "recaptcha_failed") {
          setError("reCAPTCHA verification failed. Please try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section ref={ref} className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        {/* Bg gradient */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[50%] h-[60%] bg-[radial-gradient(ellipse_at_top_left,_#E6D9FF_0%,_transparent_65%)] opacity-40" />
          <div className="absolute bottom-0 right-0 w-[40%] h-[50%] bg-[radial-gradient(ellipse_at_bottom_right,_#F0EBFF_0%,_transparent_70%)] opacity-50" />
        </div>

        <div
          className={`relative z-10 max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
            {/* Left — Info */}
            <div className="flex-1 lg:pt-6">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
                Early Access
                <br />
                <span className="text-[#9B72FF]">to Aurays</span>
              </h1>
              <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md">
                The First Online-to-Offline Dating App in Hong Kong.
              </p>

              <div className="flex flex-col gap-3 mb-10">
                {benefits.map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 bg-white border border-[#E6D9FF] rounded-2xl px-5 py-4"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E6D9FF] flex-shrink-0">
                      <i className="ri-check-line text-[#7C55CC] text-base"></i>
                    </div>
                    <span className="text-base font-semibold text-gray-800">{b}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl overflow-hidden hidden lg:block">
                <img
                  src="/aurays-offline-date-image.jpg"
                  alt="Meet naturally with Aurays"
                  className="w-full aspect-[4/3] object-cover object-top"
                />
              </div>
            </div>

            {/* Right — Form */}
            <div className="flex-1 w-full max-w-lg lg:max-w-none">
              {submitted ? (
                /* Success State */
                <div className="bg-gradient-to-br from-[#C8B6FF] to-[#E6D9FF] rounded-3xl p-10 md:p-12 text-center">
                  <div className="text-5xl mb-5">🥂</div>
                  <h2 className="text-2xl font-bold text-[#1A0A3E] mb-3">You&apos;re on the list!</h2>
                  <p className="text-[#4A2D8A] text-base leading-relaxed mb-6">
                    Thanks for joining the Aurays journey. We&apos;re working hard behind the scenes to get everything
                    ready for you.
                  </p>
                  <div className="bg-white/60 rounded-2xl px-6 py-5 mb-8">
                    <p className="text-sm text-[#4A2D8A] font-medium mb-1">Want to jump the queue?</p>
                    <p className="text-sm text-[#3A1F7A] leading-relaxed">
                      Follow us on Instagram for a sneak peek at our first O2O event locations!
                    </p>
                    <p className="text-lg font-bold text-[#1A0A3E] mt-2">@Aurays.App</p>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-gray-900 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Back to Home
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ) : (
                /* Form */
                <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    Join the waitlist for{" "}
                    <span className="text-[#9B72FF]">2 Months Free Premium Subscription!&nbsp;</span>
                  </h2>
                  <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                    We&apos;re building a space for intentional conversations and real-world dates—and we want you to be
                    part of the first wave. Enjoy{" "}
                    <span className="font-semibold text-[#9B72FF]">2 months free premium subscription</span> by being
                    the first ones to sign up here!
                  </p>

                  <form
                    id="aurays-register-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-7"
                  >
                    {/* 1. Name */}
                    <div>
                      <p className="text-xs font-bold text-[#9B72FF] mb-1 uppercase tracking-widest">01</p>
                      <label className="block text-sm font-semibold text-gray-800 mb-0.5">
                        What should we call you? <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-gray-400 mb-3">
                        Just your preferred name is fine! We like to keep things personal here.
                      </p>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="First Name / Nickname"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                      />
                    </div>

                    {/* 2. Email */}
                    <div>
                      <p className="text-xs font-bold text-[#9B72FF] mb-1 uppercase tracking-widest">02</p>
                      <label className="block text-sm font-semibold text-gray-800 mb-0.5">
                        How can we contact you? <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-gray-400 mb-3">
                        We&apos;ll send your exclusive invite and launch updates here. No spam, we promise.
                      </p>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="Email Address"
                        value={form.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                      />
                    </div>

                    {/* 3. Phone */}
                    <div>
                      <p className="text-xs font-bold text-[#9B72FF] mb-1 uppercase tracking-widest">03</p>
                      <label className="block text-sm font-semibold text-gray-800 mb-0.5">
                        What&apos;s your number? <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-gray-400 mb-3">
                        We&apos;ll need to keep the Aurays community safe, so we&apos;ll need this for account
                        verification only. You will receive a message once our app is officially launched.
                      </p>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="Country code + WhatsApp / Phone Number"
                        value={form.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                      />
                    </div>

                    {/* 4. About You */}
                    <div>
                      <p className="text-xs font-bold text-[#9B72FF] mb-1 uppercase tracking-widest">04</p>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">A little bit about you</label>

                      {/* Gender dropdown */}
                      <div className="mb-4">
                        <select
                          name="gender"
                          required
                          value={form.gender}
                          onChange={(e) => handleChange("gender", e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-[#9B72FF] transition-colors appearance-none bg-white cursor-pointer"
                        >
                          <option value="" disabled>
                            Gender
                          </option>
                          <option value="Woman">Woman</option>
                          <option value="Man">Man</option>
                          <option value="Non-binary">Non-binary</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                      </div>

                      {/* Birthday */}
                      <div>
                        <p className="text-xs text-gray-400 mb-2">
                          Aurays is an 18+ community. Plus, we might want to send a little birthday treat your way!
                        </p>
                        <input
                          type="date"
                          name="birthday"
                          required
                          value={form.birthday}
                          onChange={(e) => handleChange("birthday", e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 focus:outline-none focus:border-[#9B72FF] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="flex flex-col gap-4 pt-1">
                      {/* Age confirm - required */}
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div
                          className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 mt-0.5 transition-colors ${
                            form.ageConfirm
                              ? "bg-[#9B72FF] border-[#9B72FF]"
                              : "border-gray-300 group-hover:border-[#9B72FF]"
                          }`}
                          onClick={() => handleChange("ageConfirm", !form.ageConfirm)}
                        >
                          {form.ageConfirm && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <span className="text-sm text-gray-600 leading-snug">
                          I confirm I am 18 years of age or older. <span className="text-red-400">*</span>
                        </span>
                      </label>

                      {/* Terms - required */}
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div
                          className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 mt-0.5 transition-colors ${
                            form.terms
                              ? "bg-[#9B72FF] border-[#9B72FF]"
                              : "border-gray-300 group-hover:border-[#9B72FF]"
                          }`}
                          onClick={() => handleChange("terms", !form.terms)}
                        >
                          {form.terms && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <span className="text-sm text-gray-600 leading-snug">
                          I acknowledge that my data will be handled by Soul Media Limited as described in the{" "}
                          <Link to="/privacy-policy" className="text-[#9B72FF] font-medium hover:underline">
                            Privacy Policy
                          </Link>
                          . <span className="text-red-400">*</span>
                        </span>
                      </label>

                      {/* Marketing - optional */}
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div
                          className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 mt-0.5 transition-colors ${
                            form.marketing
                              ? "bg-[#9B72FF] border-[#9B72FF]"
                              : "border-gray-300 group-hover:border-[#9B72FF]"
                          }`}
                          onClick={() => handleChange("marketing", !form.marketing)}
                        >
                          {form.marketing && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <span className="text-sm text-gray-600 leading-snug">
                          Yes! I&apos;d love to receive early bird perks, event invites, and community updates via email
                          or WhatsApp. <span className="text-gray-400">(You can opt-out at any time).</span>
                        </span>
                      </label>
                    </div>

                    {/* Error */}
                    {error && <p className="text-sm text-red-500 bg-red-50 px-4 py-3 rounded-xl">{error}</p>}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 mt-1"
                    >
                      {loading ? "Submitting..." : "Join Now"}
                    </button>

                    <p className="text-xs text-gray-400 text-center">No credit card required &middot; Free to join</p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
